import { useEffect, useRef, useCallback } from 'react';
import type Pusher from 'pusher-js';
import type { Channel } from 'pusher-js';
import { getPusher } from '../lib/pusher';
import { chatApi } from '../store/api/chatApi';
import { baseApi } from '../store/api/baseApi';
import { useAppDispatch } from './useAppStore';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';

type MessageHandler    = (data: { conversationId: string; message: any }) => void;
type ConversationEvent = (data: any) => void;
type TypingHandler     = (data: { conversationId: string; userId: string; name: string }) => void;

interface UseChatOptions {
  onNewMessage?:       MessageHandler;
  onConversationNew?:  ConversationEvent;
  onConversationRead?: ConversationEvent;
  onTyping?:           TypingHandler;
}

export function useChat({ onNewMessage, onConversationNew, onConversationRead, onTyping }: UseChatOptions = {}) {
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);
  const dispatch    = useAppDispatch();
  const pusherRef   = useRef<Pusher | null>(null);
  const channelRef  = useRef<Channel | null>(null);
  const retryRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Ya hubo una conexión: de acá en más, cada `connected` es una RE-conexión. */
  const yaConectoRef = useRef(false);

  const invalidateConversations = useCallback(() => {
    dispatch(chatApi.util.invalidateTags(['Chat']));
  }, [dispatch]);

  /**
   * Todo lo que nos perdimos mientras estábamos afuera.
   *
   * ⛔ Re-suscribirse NO trae nada: Pusher no reenvía lo que pasó con el socket caído.
   * Cada mensaje que entró mientras la laptop dormía, cambiaba la red o el socket se
   * cortaba no disparaba ningún `invalidate`, así que el badge del sidebar quedaba con
   * el número viejo hasta que la persona recargaba a mano — el "no me llega el chat,
   * tengo que refrescar" que se reportó el 26/08/2026.
   *
   * `ChatMessages` sin id invalida las de TODAS las conversaciones, pero solo refetchea
   * la que esté montada: es la forma de que la charla abierta también se ponga al día
   * sin que este hook tenga que saber cuál es.
   */
  const revalidarTodo = useCallback(() => {
    dispatch(chatApi.util.invalidateTags(['Chat', 'ChatMessages']));
  }, [dispatch]);

  const invalidateMessages = useCallback((conversationId: string) => {
    dispatch(chatApi.util.invalidateTags([{ type: 'ChatMessages', id: conversationId }]));
  }, [dispatch]);

  const invalidateAuction = useCallback((auctionId: string) => {
    dispatch(baseApi.util.invalidateTags([{ type: 'Auction', id: auctionId }]));
  }, [dispatch]);

  useEffect(() => {
    if (!accessToken) return;

    let userId: string;
    try {
      userId = JSON.parse(atob(accessToken.split('.')[1])).sub as string;
    } catch {
      return;
    }

    const pusher = getPusher(accessToken);
    pusherRef.current = pusher;

    // El canal `private-user-{id}` es COMPARTIDO: además de este hook lo usan el banner de
    // mensajes, Phylax y los bugs (y hay DOS instancias de useChat vivas a la vez — la global
    // del layout y la de la pantalla de chat). Por eso acá NUNCA se hace `unbind_all` ni
    // `unsubscribe`: eso borraba los handlers de TODOS y, apenas se salía del /chat, el CRM
    // quedaba sin tiempo real (banner, badges, Phylax) hasta la próxima reconexión. Se atan y
    // se desatan SOLO estos handlers, por referencia. (fix 01/09/2026)
    const onSubError = () => {
      // Auth falló — reintentar en 4 s. Sin tocar el canal compartido: si la suscripción
      // falló, igual no estaba entregando nada.
      if (retryRef.current) clearTimeout(retryRef.current);
      retryRef.current = setTimeout(subscribe, 4000);
    };
    const onMessageNew = (data: { conversationId: string; message: any }) => {
      invalidateConversations();
      invalidateMessages(data.conversationId);
      onNewMessage?.(data);
      if (document.hidden && Notification.permission === 'granted') {
        const sender = data.message.sender;
        new Notification(`${sender.name} ${sender.lastName}`, {
          body: data.message.content,
          icon: '/icon-192.png',
        });
      }
    };
    const onReaction    = (data: { conversationId: string }) => invalidateMessages(data.conversationId);
    // El mensaje de una llamada cambia de estado ("Llamando…" → "Llamada · 4 min").
    const onMsgUpdated  = (data: { conversationId: string }) => { invalidateMessages(data.conversationId); invalidateConversations(); };
    const onConvNew     = (data: any) => { invalidateConversations(); onConversationNew?.(data); };
    const onConvRead    = (data: any) => { invalidateConversations(); onConversationRead?.(data); };
    const onConvRemoved = () => invalidateConversations();
    const onTypingEv    = (data: { conversationId: string; userId: string; name: string }) => onTyping?.(data);
    const onAuction     = (data: { auctionId: string }) => invalidateAuction(data.auctionId);

    const handlers: Array<[string, (data: any) => void]> = [
      ['pusher:subscription_error', onSubError],
      ['message.new',               onMessageNew],
      ['message.reaction',          onReaction],
      ['message.updated',           onMsgUpdated],
      ['conversation.new',          onConvNew],
      ['conversation.read',         onConvRead],
      ['conversation.removed',      onConvRemoved],
      ['typing',                    onTypingEv],
      ['auction.participant_new',   onAuction],
    ];

    function subscribe() {
      const channel = pusher.subscribe(`private-user-${userId}`);
      channelRef.current = channel;
      // Rebind limpio: desato y vuelvo a atar los míos, así una reconexión no los duplica.
      for (const [ev, fn] of handlers) { channel.unbind(ev, fn); channel.bind(ev, fn); }
    }

    subscribe();

    /**
     * Reconectar si Pusher pierde y recupera la conexión.
     *
     * La PRIMERA vez no se revalida: `connected` llega enseguida después del
     * `subscribe()` de arriba y sería un pedido de más apenas abre el CRM. De la segunda
     * en adelante sí, porque ahí ya hubo un hueco en el medio.
     */
    function alConectar() {
      subscribe();
      if (yaConectoRef.current) revalidarTodo();
      yaConectoRef.current = true;
    }
    pusher.connection.bind('connected', alConectar);

    return () => {
      if (retryRef.current) clearTimeout(retryRef.current);
      pusher.connection.unbind('connected', alConectar);
      // Solo MIS handlers; el canal compartido queda vivo para el resto (banner, Phylax, bugs).
      const ch = channelRef.current;
      if (ch) for (const [ev, fn] of handlers) ch.unbind(ev, fn);
    };
  }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  return { pusher: pusherRef.current };
}

/** Pedir permiso de notificaciones browser al iniciar sesión */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'default') {
    await Notification.requestPermission();
  }
}
